import { forwardRef, useEffect, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { ptBR } from "react-day-picker/locale";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  formatDisplayDate,
  parseIsoDate,
  toIsoDate,
} from "@/lib/dateInput";

export interface DatePickerProps {
  value?: string;
  onChange?: (isoDate: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  align?: "start" | "center" | "end";
  modal?: boolean;
  id?: string;
  "aria-label"?: string;
  "aria-required"?: boolean;
  "aria-invalid"?: boolean;
}

export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  function DatePicker(
    {
      value = "",
      onChange,
      disabled = false,
      placeholder = "Selecione uma data",
      className,
      triggerClassName,
      align = "start",
      modal = false,
      id,
      "aria-label": ariaLabel,
      "aria-required": ariaRequired,
      "aria-invalid": ariaInvalid,
    },
    ref
  ) {
    const [open, setOpen] = useState(false);
    const selectedDate = parseIsoDate(value);
    const [month, setMonth] = useState<Date>(() => selectedDate ?? new Date());

    useEffect(() => {
      if (selectedDate) {
        setMonth(selectedDate);
      }
    }, [value]);

    const displayValue = selectedDate ? formatDisplayDate(value) : placeholder;

    const handleSelect = (date: Date | undefined) => {
      if (!date) return;
      onChange?.(toIsoDate(date));
      setOpen(false);
    };

    const handleOpenChange = (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        setMonth(selectedDate ?? new Date());
      }
    };

    return (
      <Popover open={open} onOpenChange={handleOpenChange} modal={modal}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            data-empty={!selectedDate}
            aria-label={ariaLabel ?? displayValue}
            aria-required={ariaRequired}
            aria-invalid={ariaInvalid}
            className={cn(
              "h-auto w-full justify-start rounded-none border-primary-200 bg-white px-4 py-3 font-light",
              "text-primary-950 hover:bg-primary-50 dark:border-primary-800 dark:bg-primary-950 dark:text-white dark:hover:bg-primary-900",
              "data-[empty=true]:text-primary-500 dark:data-[empty=true]:text-primary-400",
              triggerClassName
            )}
          >
            <CalendarIcon className="mr-2 size-4 shrink-0 opacity-70" aria-hidden />
            <span>{displayValue}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "w-auto rounded-none border-primary-200 p-0 dark:border-primary-800",
            className
          )}
          align={align}
          side="bottom"
          collisionPadding={16}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            month={month}
            onMonthChange={setMonth}
            onSelect={handleSelect}
            locale={ptBR}
            disabled={disabled}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  }
);
